class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class SearchTyped extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
  }

  async init() {
    this.insertCursor();
    await this.start(this.getAttribute('data-first-text'), this.startDelay);

    setTimeout(async () => {
      await this.reset();
      await this.start(this.getAttribute('data-last-text'), 0);
    }, 600);
  }

  async start(text, delay) {
    this.innerHTML = text;
    await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
    this.cursor.classList.add('blink');
  }

  async reset() {
    this.cursor.classList.remove('blink');
    await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
  }

  insertCursor() {
    if (this.cursor) return;

    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', true);
    this.cursor.innerHTML = '|';
    this.parentElement.insertBefore(this.cursor, this.nextSibling);
  }
}
customElements.define('search-typed', SearchTyped);

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('click', this.hideTypewriter.bind(this));
    this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  hideTypewriter() {
    if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    if (this.closest('.collection')) {
      document.body.classList.add('predictive-search-open');

      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) return;

      const url = this.buildUrl().toString();
      this.renderSection(url, event);
    }
  }

  clear(event = null) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  getQuery() {
    return this.input.value.trim();
  }

  buildUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.hideTypewriter();

    const url = this.buildUrl().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);

    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    
    this.setAttribute('loading', '');

    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);

        this.removeAttribute('loading');
        this.setAttribute('results', '');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        }
        else {
          console.error(error);
        }
      });
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
    if (document.getElementById(id) === null) return;

    document.getElementById(id).innerHTML = new DOMParser()
      .parseFromString(responseText, 'text/html')
      .getElementById(id).innerHTML;
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search-overlay', PredictiveSearchOverlay);


const translations = {
  ka: { 
    inStock: 'მარაგშია',
    outOfStock: 'არ არის მარაგში',
    outOfStockBadge: 'არ არის მარაგში',
    product: 'პროდუქტი',
    products: 'პროდუქტი',
    variants: 'variants',
    noProductsFound: 'პროდუქტი ვერ მოიძებნა'
  },
  en: { // English
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    outOfStockBadge: 'Out of Stock',
    product: 'Product',
    products: 'Products',
    variants: 'variants',
    noProductsFound: 'No products found'
  },
  de: { // German
    inStock: 'Auf Lager',
    outOfStock: 'Nicht vorrätig',
    outOfStockBadge: 'Nicht vorrätig',
    product: 'Produkt',
    products: 'Produkte',
    variants: 'Varianten',
    noProductsFound: 'Keine Produkte gefunden'
  },
  ru: { // Russian
    inStock: 'В наличии',
    outOfStock: 'Нет в наличии',
    outOfStockBadge: 'Нет в наличии',
    product: 'Продукт',
    products: 'Продукты',
    variants: 'варианты',
    noProductsFound: 'Продукты не найдены'
  },
  tr: { // Turkish
    inStock: 'Stokta',
    outOfStock: 'Stokta Yok',
    outOfStockBadge: 'Stokta Yok',
    product: 'Ürün',
    products: 'Ürünler',
    variants: 'varyantlar',
    noProductsFound: 'Ürün bulunamadı'
  }
};

function getCurrentLanguage() {
  // First check Shopify.locale which is set globally by Shopify
  if (typeof Shopify !== 'undefined' && Shopify.locale) {
    const shopifyLocale = Shopify.locale.toLowerCase();
    // Map Shopify locale codes to our supported languages
    const localeMap = {
      'ka': 'ka',
      'ka-ge': 'ka',
      'en': 'en',
      'en-us': 'en',
      'en-gb': 'en',
      'de': 'de',
      'de-de': 'de',
      'ru': 'ru',
      'ru-ru': 'ru',
      'tr': 'tr',
      'tr-tr': 'tr'
    };
    
    if (localeMap[shopifyLocale]) {
      return localeMap[shopifyLocale];
    }
  }
  
  const path = window.location.pathname;
  const match = path.match(/^\/(en|de|ru|tr)\//);
  return match ? match[1] : 'ka'; 
}

function getTranslation(key) {
  const currentLang = getCurrentLanguage();
  return translations[currentLang] ? translations[currentLang][key] : translations['ka'][key];
}

let cachedProducts = null;

async function fetchProductData() {
  try {
    const lang = getCurrentLanguage();
    
    let url;
    if (lang === 'ka') {
      url = 'https://meama-georgia.myshopify.com/collections/all-products/products.json?limit=250';
    } else {
      url = `https://meama-georgia.myshopify.com/${lang}/collections/all-products/products.json?limit=250`;
    }

    const response = await fetch(url);
    const data = await response.json();

    cachedProducts = data.products;
    return data.products;
  } catch (error) {
    console.error("Error fetching products:", error);
    return null;
  }
}

function getLanguagePrefix() {
  const lang = getCurrentLanguage();
  return lang !== 'ka' ? `/${lang}` : '';
}

function clearSearchResults() {
  const existingResults = document.querySelectorAll('.search-results');
  existingResults.forEach(result => result.remove());
}

function displaySearchResults(matches) {
  clearSearchResults();
  
  const searchForm = document.querySelector('.search-drawer .search__form');
  if (!searchForm) return;
  
  if (matches.length === 0) {
    const noResults = document.createElement('div');
    noResults.className = 'search-results no-results';
    noResults.innerHTML = `
      <div class="no-results-message">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p>${getTranslation('noProductsFound')}</p>
        <span></span>
      </div>
    `;
    searchForm.appendChild(noResults);
    return;
  }

  const resultsContainer = document.createElement('div');
  resultsContainer.className = 'search-results';
  
  const resultsHeader = document.createElement('div');
  resultsHeader.className = 'search-results-header';
  const productWord = matches.length === 1 ? getTranslation('product') : getTranslation('products');
  resultsHeader.innerHTML = `
    <span class="results-count">${matches.length} ${productWord}</span>
  `;
  resultsContainer.appendChild(resultsHeader);
  
  const resultsGrid = document.createElement('div');
  resultsGrid.className = 'search-results-grid';
  
  matches.forEach((match, index) => {
    const product = match.product;
    const variant = match.variant;
    
    const productItem = document.createElement('div');
    productItem.className = 'product-item';
    productItem.style.animationDelay = `${index * 0.05}s`;
    
    let price, comparePrice, available;
    if (match.type === 'variant') {
      price = parseFloat(variant.price);
      comparePrice = variant.compare_at_price ? parseFloat(variant.compare_at_price) : null;
      available = variant.available;
    } else {
      price = parseFloat(product.variants[0].price);
      comparePrice = product.variants[0].compare_at_price ? parseFloat(product.variants[0].compare_at_price) : null;
      available = product.variants.some(v => v.available);
    }
    
    const discountPercent = comparePrice && comparePrice > price ? 
      Math.round(((comparePrice - price) / comparePrice) * 100) : 0;
    
    if (match.type === 'variant') {
      productItem.innerHTML = `
        <a href="${getLanguagePrefix()}/products/${product.handle}" class="product-link">
          <div class="product-image-container">
            <div class="product-image">
              ${variant.featured_image ? 
                `<img src="${variant.featured_image.src}" alt="${variant.title}" loading="lazy">` : 
                product.images[0] ? 
                  `<img src="${product.images[0].src}" alt="${product.title}" loading="lazy">` : 
                  '<div class="no-image"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21,15 16,10 5,21"></polyline></svg></div>'
              }
            </div>
            ${!available ? `<div class="out-of-stock-badge">${getTranslation('outOfStockBadge')}</div>` : ''}
            ${discountPercent > 0 ? `<div class="discount-badge">-${discountPercent}%</div>` : ''}
          </div>
          <div class="product-info">
            <div class="product-title-container">
              <h4 class="product-title">${variant.title}</h4>
              <div class="product-subtitle">${product.title}</div>
            </div>
            <div class="product-pricing">
              <div class="product-price">${price.toFixed(2)} ₾</div>
              ${comparePrice && comparePrice > price ? 
                `<div class="product-compare-price">${comparePrice.toFixed(2)} ₾</div>` : ''
              }
            </div>
            <div class="product-availability ${available ? 'available' : 'unavailable'}">
              <div class="availability-indicator"></div>
              <span>${available ? getTranslation('inStock') : getTranslation('outOfStock')}</span>
            </div>
          </div>
        </a>
      `;
    } else {
      productItem.innerHTML = `
       <a href="${getLanguagePrefix()}/products/${product.handle}" class="product-link">
          <div class="product-image-container">
            <div class="product-image">
              ${product.images[0] ? 
                `<img src="${product.images[0].src}" alt="${product.title}" loading="lazy">` : 
                '<div class="no-image"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21,15 16,10 5,21"></polyline></svg></div>'
              }
            </div>
            ${!available ? `<div class="out-of-stock-badge">${getTranslation('outOfStockBadge')}</div>` : ''}
            ${discountPercent > 0 ? `<div class="discount-badge">-${discountPercent}%</div>` : ''}
          </div>
          <div class="product-info">
            <div class="product-title-container">
              <h4 class="product-title">${product.title}</h4>
              ${product.variants.length > 1 ? 
                `<div class="product-variants">${product.variants.length} ${getTranslation('variants')}</div>` : ''
              }
            </div>
            <div class="product-pricing">
              <div class="product-price">${price.toFixed(2)} ₾</div>
              ${comparePrice && comparePrice > price ? 
                `<div class="product-compare-price">${comparePrice.toFixed(2)} ₾</div>` : ''
              }
            </div>
            <div class="product-availability ${available ? 'available' : 'unavailable'}">
              <div class="availability-indicator"></div>
              <span>${available ? getTranslation('inStock') : getTranslation('outOfStock')}</span>
            </div>
          </div>
        </a>
      `;
    }
    
    resultsGrid.appendChild(productItem);
  });
  
  resultsContainer.appendChild(resultsGrid);
  searchForm.appendChild(resultsContainer);
  
 addSearchStyles();
}

function addSearchStyles() {
  if (document.getElementById('search-styles')) return;

  const style = document.createElement('style');
  style.id = 'search-styles';
  style.textContent = `
  
.search-results {
  margin-top: 12px;
}
.search-results-header {
  margin-bottom: 16px;
  text-align: center;
}
.results-count {
  font-size: 14px;
  font-weight: 500;
  color: #666;
}
.search-results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}
.product-item {
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.3s ease forwards;
  opacity: 0;
}
.product-link {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
}
.product-image-container {
  position: relative;
}
.product-image {
  display: flex;
  justify-content: center;
  align-items: center;
}
.product-image img {
  width: 100%;
  display: block;
  border-radius: 8px;
  object-fit: cover;
}
.no-image {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 180px;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #ccc;
}
.out-of-stock-badge, .discount-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(255,0,0,0.85);
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.product-info {
  margin-top: 6px;
}
.product-title-container {
  display: flex;
  flex-direction: column;
}
.product-price {
  font-weight: bold;
  margin-top: 4px;
}
.product-compare-price {
  text-decoration: line-through;
  margin-left: 6px;
  color: gray;
}
.product-availability span {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-top: 2px;
}

.product-availability.available span {
  color: #28a745; /* green */
}

.product-availability.unavailable span {
  color: #dc3545; /* red */
}

.availability-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: green;
  margin-right: 4px;
}
.unavailable .availability-indicator {
  background-color: red;
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-results-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .product-image {
    text-align: center;
  }
  
  .product-image img {
    max-width: 100%;
    height: auto;
    margin: 0 auto;
  }
  
  .no-image {
    height: 150px;
    margin: 0 auto;
  }
}

@keyframes fadeIn {
  to { opacity: 1; }
}
  `;
  document.head.appendChild(style);
}


function normalizeText(text) {
  return (text || '')
    .toLowerCase()
    .replace(/\s+/g, ''); // ignore spaces
}

function clearSearchResults() {
  const existingResults = document.querySelectorAll('.search-results');
  existingResults.forEach(result => result.remove());
}

// ----- SIMPLE SEARCH -----
function simpleSearch(searchTerm) {
  if (!cachedProducts || searchTerm.length < 2) {
    clearSearchResults();
    return;
  }

  const searchTermNorm = normalizeText(searchTerm);
  let matches = [];

  cachedProducts.forEach(product => {
    const titleNorm = normalizeText(product.title);
    const handleNorm = normalizeText(product.handle);
    const productTypeNorm = normalizeText(product.product_type || '');

    // Match both starts-with and includes
    if (
      titleNorm.startsWith(searchTermNorm) ||
      handleNorm.startsWith(searchTermNorm) ||
      productTypeNorm.startsWith(searchTermNorm) ||
      titleNorm.includes(searchTermNorm) ||
      handleNorm.includes(searchTermNorm) ||
      productTypeNorm.includes(searchTermNorm)
    ) {
      matches.push({ type: 'product', product });
    }

    product.variants.forEach(variant => {
      if (variant.available) {
        const variantTitleNorm = normalizeText(variant.title);
        if (
          variantTitleNorm.startsWith(searchTermNorm) ||
          variantTitleNorm.includes(searchTermNorm)
        ) {
          const isDuplicate = matches.some(match =>
            match.type === 'variant' &&
            match.product.id === product.id &&
            match.variant.id === variant.id
          );
          if (!isDuplicate) {
            matches.push({ type: 'variant', product, variant });
          }
        }
      }
    });
  });

  matches = matches.slice(0, 100);
  displaySearchResults(matches);
}

function normalizeText(text) {
  if (!text) return '';

  let lower = text.toLowerCase();

  // Replace hyphens with nothing
  lower = lower.replace(/-/g, '');

  // Remove all spaces
  lower = lower.replace(/\s+/g, '');

  // Transliterate Latin letters to Georgian
  return transliterate(lower);
}

function transliterate(text) {
  const multiMap = {
    'tch': 'ჭ', 'sh': 'შ', 'ch': 'ჩ', 'dz': 'ძ', 'ts': 'ც',
    'kh': 'ხ', 'gh': 'ღ', 'ph': 'ფ', 'zh': 'ჟ'
  };

  // Replace multi-letter sequences first
  let result = text;
  for (const [key, value] of Object.entries(multiMap)) {
    result = result.replace(new RegExp(key, 'g'), value);
  }

  const singleMap = {
    'a': 'ა', 'b': 'ბ', 'g': 'გ', 'd': 'დ', 'e': 'ე', 'v': 'ვ',
    'z': 'ზ', 't': 'ტ', 'i': 'ი', 'k': 'კ', 'l': 'ლ', 'm': 'მ',
    'n': 'ნ', 'o': 'ო', 'p': 'პ', 'j': 'ჯ', 'r': 'რ', 's': 'ს',
    'u': 'უ', 'f': 'ფ', 'q': 'ქ', 'y': 'ყ', 'h': 'ჰ'
  };

  let finalResult = '';
  for (const char of result) {
    finalResult += singleMap[char] || char;
  }

  return finalResult;
}

document.addEventListener('DOMContentLoaded', function() {
  fetchProductData();
  
  const searchInput = document.querySelector('.search-drawer .search__form .search__field input');
  
  if (searchInput) {
    searchInput.addEventListener('input', function(e) {
      const searchTerm = e.target.value;
      simpleSearch(searchTerm);
    });
    
    const searchForm = document.querySelector('.search-drawer .search__form');
    if (searchForm) {
      searchForm.addEventListener('submit', function(e) {
        const searchTerm = searchInput.value.trim();
        if (searchTerm.length > 0) {
          // Allow form submission to go to search results page
          const languagePrefix = getLanguagePrefix();
          const searchUrl = `${languagePrefix}/search?q=${encodeURIComponent(searchTerm)}`;
          window.location.href = searchUrl;
        }
        e.preventDefault();
        return false;
      });
    }
    
    searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        const searchTerm = searchInput.value.trim();
        if (searchTerm.length > 0) {
          const languagePrefix = getLanguagePrefix();
          const searchUrl = `${languagePrefix}/search?q=${encodeURIComponent(searchTerm)}`;
          window.location.href = searchUrl;
        }
      }
    });
    
    const searchButtons = document.querySelectorAll('.search-drawer button[type="submit"], .search-drawer .search-button');
    searchButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        const searchTerm = searchInput.value.trim();
        if (searchTerm.length > 0) {
          const languagePrefix = getLanguagePrefix();
          const searchUrl = `${languagePrefix}/search?q=${encodeURIComponent(searchTerm)}`;
          window.location.href = searchUrl;
        }
        return false;
      });
    });
  }
});
